import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiSave, FiX, FiInfo } from 'react-icons/fi';
import Swal from 'sweetalert2';

const API_URL = 'https://app.thundermaxhome.com/api.php';

export default function ProductsManagementPage() {
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'

  const [formData, setFormData] = useState({
    id: '',
    model_name: '',
    brand: 'Daikin',
    btu: '',
    specs: '',
    price: '',
    stock: ''
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    setIsLoading(true);
    try {
      const res = await fetch(`${API_URL}?action=get_products`);
      const data = await res.json();
      if (data.success) {
        setProducts(data.data || []);
      }
    } catch (err) {
      console.error(err);
      Swal.fire('ดึงข้อมูลล้มเหลว', 'ไม่สามารถเชื่อมต่อดึงรายการสินค้าได้', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenAdd = () => {
    setModalMode('add');
    setFormData({
      id: '',
      model_name: '',
      brand: 'Daikin',
      btu: 9000,
      specs: 'Inverter, ประหยัดไฟเบอร์ 5, สารทำความเย็น R32',
      price: 12000,
      stock: 10
    });
    setShowModal(true);
  };

  const handleOpenEdit = (product) => {
    setModalMode('edit');
    setFormData({
      id: product.id,
      model_name: product.model_name,
      brand: product.brand,
      btu: product.btu,
      specs: product.specs,
      price: product.price,
      stock: product.stock
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const action = modalMode === 'add' ? 'add_product' : 'edit_product';
    Swal.fire({ title: 'กำลังบันทึกข้อมูล...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });

    try {
      const res = await fetch(`${API_URL}?action=${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await res.json();

      if (data.success) {
        Swal.fire({ icon: 'success', title: 'บันทึกสำเร็จ!', showConfirmButton: false, timer: 1500 });
        setShowModal(false);
        fetchProducts();
      } else {
        throw new Error(data.message);
      }
    } catch (err) {
      Swal.fire('ข้อผิดพลาด', err.message || 'บันทึกข้อมูลไม่สำเร็จ', 'error');
    }
  };

  const handleDelete = async (id, modelName) => {
    const result = await Swal.fire({
      title: 'ต้องการลบสินค้า?',
      text: `คุณแน่ใจว่าต้องการลบสินค้า "${modelName}" ออกจากคลังสต๊อกหรือไม่`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'ลบข้อมูล',
      cancelButtonText: 'ยกเลิก'
    });

    if (result.isConfirmed) {
      Swal.fire({ title: 'กำลังลบ...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
      try {
        const res = await fetch(`${API_URL}?action=delete_product`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id })
        });
        const data = await res.json();
        if (data.success) {
          Swal.fire({ icon: 'success', title: 'ลบสำเร็จ!', showConfirmButton: false, timer: 1500 });
          fetchProducts();
        } else {
          throw new Error(data.message);
        }
      } catch (err) {
        Swal.fire('ล้มเหลว', err.message || 'ไม่สามารถลบข้อมูลได้', 'error');
      }
    }
  };

  return (
    <div className="space-y-6 pb-10 animate-fade-in text-gray-800 dark:text-slate-100">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-black">📦 จัดการสินค้าและสต๊อก (Real-time Stocks)</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">เพิ่ม แก้ไข ลบ และปรับปรุงสต๊อกแอร์สำหรับ E-Catalog ทีมเซลส์</p>
        </div>
        <button onClick={handleOpenAdd} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg text-sm font-bold flex items-center gap-2 shadow-sm transition">
          <FiPlus /> เพิ่มสินค้าลงคลัง
        </button>
      </div>

      <div className="bg-white dark:bg-[#1e293b] rounded-xl border border-gray-200 dark:border-[#334155] shadow-sm overflow-hidden transition-colors">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="text-xs bg-gray-100 dark:bg-[#0f172a] text-gray-600 dark:text-gray-400 border-b border-gray-200 dark:border-[#334155] transition-colors">
              <tr>
                <th className="px-6 py-4 font-medium" style={{ width: '15%' }}>แบรนด์</th>
                <th className="px-6 py-4 font-medium" style={{ width: '35%' }}>รุ่นสินค้า</th>
                <th className="px-6 py-4 font-medium text-center" style={{ width: '10%' }}>BTU</th>
                <th className="px-6 py-4 font-medium text-right" style={{ width: '15%' }}>ราคาจำหน่าย</th>
                <th className="px-6 py-4 font-medium text-center" style={{ width: '10%' }}>คงเหลือในคลัง</th>
                <th className="px-6 py-4 font-medium text-center" style={{ width: '15%' }}>ดำเนินการ</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-[#334155] transition-colors">
              {isLoading ? (
                <tr>
                  <td colSpan="6" className="text-center py-10 font-bold text-blue-500 bg-transparent">
                    กำลังโหลดคลังสินค้า...
                  </td>
                </tr>
              ) : products.length > 0 ? (
                products.map((product) => (
                  <tr key={product.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                    <td className="px-6 py-4">
                      <span className="bg-blue-500/10 text-blue-500 dark:text-blue-400 border border-blue-500/20 px-2 py-0.5 rounded text-xs font-bold uppercase">
                        {product.brand}
                      </span>
                    </td>
                    <td className="px-6 py-4 font-bold">
                      <div>{product.model_name}</div>
                      <div className="text-[10px] text-gray-400 dark:text-gray-500 font-normal mt-0.5">{product.specs}</div>
                    </td>
                    <td className="px-6 py-4 text-center font-semibold">
                      {product.btu.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-right font-bold text-emerald-600 dark:text-emerald-400">
                      ฿{Number(product.price).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`px-2.5 py-1 rounded-md text-xs font-bold ${product.stock > 10 ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' : product.stock > 0 ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400' : 'bg-red-500/10 text-red-600 dark:text-red-400'}`}>
                        {product.stock} เครื่อง
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex justify-center gap-2">
                        <button onClick={() => handleOpenEdit(product)} className="p-2 text-slate-500 hover:text-blue-500 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition" title="แก้ไข">
                          <FiEdit2 size={14} />
                        </button>
                        <button onClick={() => handleDelete(product.id, product.model_name)} className="p-2 text-slate-500 hover:text-red-500 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition" title="ลบ">
                          <FiTrash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="text-center py-10 text-gray-400">
                    ไม่มีสินค้าในคลังสต๊อก
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 📦 Modal เพิ่ม/แก้ไขสินค้า */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-[#1a2236] w-full max-w-lg rounded-2xl shadow-2xl border border-gray-200 dark:border-[#334155] overflow-hidden animate-zoom-in">
            <div className="p-5 border-b border-gray-200 dark:border-[#334155] flex justify-between items-center bg-gray-50 dark:bg-[#131825]">
              <h3 className="font-black text-base text-gray-800 dark:text-white">
                {modalMode === 'add' ? '➕ เพิ่มสินค้าใหม่ลงคลัง' : '✏️ แก้ไขรายละเอียดสินค้า'}
              </h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-white p-1 rounded-lg hover:bg-slate-800 transition">
                <FiX size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-500 dark:text-gray-400">แบรนด์สินค้า</label>
                  <select value={formData.brand} onChange={e => setFormData({...formData, brand: e.target.value})} className="w-full bg-gray-50 dark:bg-[#202940] border border-gray-300 dark:border-[#334155] text-sm rounded-lg p-2.5 focus:border-blue-500 outline-none text-gray-800 dark:text-white">
                    <option value="Daikin">Daikin</option>
                    <option value="Mitsubishi">Mitsubishi</option>
                    <option value="Carrier">Carrier</option>
                    <option value="Panasonic">Panasonic</option>
                    <option value="Haier">Haier</option>
                    <option value="LG">LG</option>
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-500 dark:text-gray-400">ขนาด BTU</label>
                  <input type="number" required value={formData.btu} onChange={e => setFormData({...formData, btu: Number(e.target.value)})} className="w-full bg-gray-50 dark:bg-[#202940] border border-gray-300 dark:border-[#334155] text-sm rounded-lg p-2.5 focus:border-blue-500 outline-none text-gray-800 dark:text-white" />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-500 dark:text-gray-400">ชื่อรุ่นสินค้า</label>
                <input type="text" required value={formData.model_name} onChange={e => setFormData({...formData, model_name: e.target.value})} className="w-full bg-gray-50 dark:bg-[#202940] border border-gray-300 dark:border-[#334155] text-sm rounded-lg p-2.5 focus:border-blue-500 outline-none text-gray-800 dark:text-white" placeholder="เช่น Max Inverter (FTKQ12XV2S)" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-500 dark:text-gray-400">ราคาจำหน่าย (บาท)</label>
                  <input type="number" required value={formData.price} onChange={e => setFormData({...formData, price: Number(e.target.value)})} className="w-full bg-gray-50 dark:bg-[#202940] border border-gray-300 dark:border-[#334155] text-sm rounded-lg p-2.5 focus:border-blue-500 outline-none text-gray-800 dark:text-white" />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-500 dark:text-gray-400">จำนวนสต๊อกสินค้า</label>
                  <input type="number" required value={formData.stock} onChange={e => setFormData({...formData, stock: Number(e.target.value)})} className="w-full bg-gray-50 dark:bg-[#202940] border border-gray-300 dark:border-[#334155] text-sm rounded-lg p-2.5 focus:border-blue-500 outline-none text-gray-800 dark:text-white" />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-500 dark:text-gray-400">รายละเอียดสินค้าและจุดเด่น</label>
                <textarea rows="3" value={formData.specs} onChange={e => setFormData({...formData, specs: e.target.value})} className="w-full bg-gray-50 dark:bg-[#202940] border border-gray-300 dark:border-[#334155] text-sm rounded-lg p-2.5 focus:border-blue-500 outline-none text-gray-800 dark:text-white" placeholder="เช่น ระบบ Inverter, ประหยัดไฟเบอร์ 5 ระดับ 1 ดาว"></textarea>
              </div>

              <button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-lg shadow-md transition flex justify-center items-center gap-2 mt-4">
                <FiSave /> บันทึกข้อมูลสินค้า
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
